/**
 * 12-Week Strength Foundations (Beginner)
 *
 * For users who pick the `strength` goal at onboarding AND tag themselves
 * as `beginner`. The intermediate Pure Strength program assumes the
 * lifter already has a 1RM, comfortable form on the four main barbell
 * lifts, and tolerance for ~80% loading. A true novice has none of that.
 *
 * Methodology — Linear Novice Progression (NLP), patterned on the
 * StrongLifts 5×5 / Starting Strength tradition:
 * - 3 days/week, full body, alternating Workout A and Workout B
 * - Squat hits 3×/week (the fast novice-progression frequency)
 * - 5 main movements only: Back Squat, Bench Press, Overhead Press,
 *   Deadlift, Barbell Row. Cover the whole body, learn the patterns.
 * - 3×5 working sets — fewer reps means more practice of the start of
 *   each rep, where novices learn the most about positioning.
 * - Loads start light enough to be embarrassing. The point is reps with
 *   perfect form, not impressive numbers. Plates can come later.
 *
 * Periodization is intentionally boring: a single linear ramp from
 * 55% → 80% across the program, with a Form Check cutback at W6 where
 * loads drop 15% so users can re-groove technique that creeps off as
 * weight rises. Most novices don't need waved periodization — they need
 * consistent reps with weights that gradually increase. The Pure
 * Strength program (intermediate) introduces the wave model when this
 * one stops producing PRs (typically W8-12).
 */

import type { WorkoutProgram, WorkoutTemplateDay, ProgramMetadata, ProgramPeriodizationModel } from '../types/program';

const metadata: ProgramMetadata = {
  id: 'strength-foundations-12week',
  name: '12-Week Strength Foundations',
  description: 'Linear progression for new lifters — three days a week, five main lifts, add weight every session. Builds the technique foundation that every later strength program depends on.',
  level: 'recruit',
  duration: 12,
  daysPerWeek: 3,
  goals: ['strength'],
  difficulty: 'beginner',
  version: '1.0.0',
  createdAt: new Date(),
  updatedAt: new Date(),
  isActive: true,
  hasRangeDay: false,
};

const periodization: ProgramPeriodizationModel = {
  style: 'linear',
  phases: [
    // Weeks 1 + 12: Testing
    {
      weeks: [1, 12],
      type: 'assessment',
      name: 'Strength Testing',
      focus: 'Light load benchmarks at W1 (movement quality), 5RM retest at W12',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
    },
    // Weeks 2-5: Foundation Phase. Light loads, perfect reps, build the
    // habit and the technique. Intensity is low because beginners get
    // strong from frequency and consistency, not from grinding heavy.
    {
      weeks: [2, 3, 4, 5],
      type: 'accumulation',
      name: 'Foundation Phase',
      focus: 'Light loads + perfect reps. Add weight every session.',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
      progression: {
        type: 'linear',
        startIntensity: 0.55,
        endIntensity: 0.68,
        weeklyIncrease: 0.0325,
      },
      trainingParameters: {
        repRange: [5, 5],
        setsMultiplier: 1.0,
        restPeriod: '2-3min',
      },
    },
    // Week 6: Form Check Cutback. Loads drop 15% so users can re-feel
    // the lifts at moderate weights — most novices have technique drift
    // creep in by week 5 that they don't notice until they reset.
    {
      weeks: [6],
      type: 'deload',
      name: 'Form Check Cutback',
      focus: 'Drop 15% on every lift, focus 100% on technique.',
      intensityMultiplier: 0.85,
      volumeMultiplier: 0.8,
      progression: {
        type: 'linear',
        startIntensity: 0.60,
        endIntensity: 0.60,
        weeklyIncrease: 0,
      },
      trainingParameters: {
        repRange: [5, 5],
        setsMultiplier: 0.8,
        restPeriod: '2-3min',
      },
    },
    // Weeks 7-11: Build Phase. Loads ramp from 70% to 80% — by W11 the
    // lifter is approaching real working weight. Stalls in this phase
    // are expected and signal readiness for Pure Strength's wave model.
    {
      weeks: [7, 8, 9, 10, 11],
      type: 'accumulation',
      name: 'Build Phase',
      focus: 'Working loads. Progress every session until you stall.',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
      progression: {
        type: 'linear',
        startIntensity: 0.70,
        endIntensity: 0.80,
        weeklyIncrease: 0.02,
      },
      trainingParameters: {
        repRange: [5, 5],
        setsMultiplier: 1.0,
        restPeriod: '2-3min',
      },
    },
  ],
};

// BASE TEMPLATES

const testingDay: WorkoutTemplateDay = {
  name: 'Strength Test',
  sessionType: 'assessment',
  estimatedDuration: 60,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 8, format: 'recovery', details: 'Spine mobility — slow and controlled' },
        { name: 'Hip Opener Flow', sets: 1, reps: 10, format: 'recovery', details: 'Open the hips before squatting' },
        { name: 'Band Pull-Aparts', sets: 2, reps: 15, format: 'recovery', details: 'Wake up the upper back' },
      ],
    },
    {
      name: 'Light Benchmarks',
      type: 'testing',
      exercises: [
        { name: 'Back Squat', sets: 1, reps: '5RM', intensity: 'Tech', details: 'Find a load you can do for 5 reps with PERFECT form. Not grinding — clean reps. This is your starting weight.', sessionType: 'assessment' },
        { name: 'Bench Press', sets: 1, reps: '5RM', intensity: 'Tech', details: 'Same — 5 perfect reps with a spotter. No grinder reps.', sessionType: 'assessment' },
        { name: 'Overhead Press', sets: 1, reps: '5RM', intensity: 'Tech', details: 'Strict press, full lockout. Form first.', sessionType: 'assessment' },
        { name: 'Deadlift', sets: 1, reps: '5RM', intensity: 'Tech', details: 'Reset between reps — no touch-and-go. Neutral spine.', sessionType: 'assessment' },
        { name: 'Barbell Row', sets: 1, reps: '5RM', intensity: 'Tech', details: 'Bent-over, brace hard, pull to lower chest. Strict.', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 10, format: 'recovery', details: 'Decompress the spine after lifting' },
      ],
    },
  ],
};

// Workout A — Squat-Bench-Row. The horizontal-pressing day.
const workoutADay: WorkoutTemplateDay = {
  name: 'Workout A — Squat / Bench / Row',
  sessionType: 'pt',
  estimatedDuration: 55,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 8, format: 'recovery', details: 'Spine mobility before loading' },
        { name: 'Hip Opener Flow', sets: 1, reps: 10, format: 'recovery', details: 'Open hips before squatting' },
        { name: 'Band Pull-Aparts', sets: 2, reps: 15, format: 'recovery', details: 'Wake up upper back for pressing and rowing' },
      ],
    },
    {
      name: 'Main Lifts',
      type: 'strength',
      exercises: [
        { name: 'Back Squat', sets: 3, reps: 5, intensity: 'progressive', details: 'Add 10 lb from last session. If you missed reps last time, repeat the same weight.' },
        { name: 'Bench Press', sets: 3, reps: 5, intensity: 'progressive', details: 'Pause briefly on chest. Add 5 lb from last session if all reps were clean.' },
        { name: 'Barbell Row', sets: 3, reps: 5, intensity: 'progressive', details: 'Bent-over, strict form. Pull to lower chest.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Hip Mobility Flow', sets: 2, reps: 8, format: 'recovery', details: 'Decompress hips and spine after squatting' },
      ],
    },
  ],
};

// Workout B — Squat-OHP-Deadlift. The vertical-pressing day. Deadlift
// only does ONE working set because triple-set deadlifts at this volume
// will fry recovery for the next squat day.
const workoutBDay: WorkoutTemplateDay = {
  name: 'Workout B — Squat / OHP / Deadlift',
  sessionType: 'pt',
  estimatedDuration: 60,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 8, format: 'recovery', details: 'Spine mobility before loading' },
        { name: 'Hip Opener Flow', sets: 1, reps: 10, format: 'recovery', details: 'Critical before deadlifts — get the hips moving' },
        { name: 'Band Pull-Aparts', sets: 2, reps: 15, format: 'recovery', details: 'Stabilize shoulders for the overhead press' },
      ],
    },
    {
      name: 'Main Lifts',
      type: 'strength',
      exercises: [
        { name: 'Back Squat', sets: 3, reps: 5, intensity: 'progressive', details: 'Same as Workout A — add 10 lb from last squat session.' },
        { name: 'Overhead Press', sets: 3, reps: 5, intensity: 'progressive', details: 'Strict — no leg drive. Add 5 lb from last session if all reps were clean.' },
        { name: 'Deadlift', sets: 1, reps: 5, intensity: 'progressive', details: 'ONE working set of 5 — that\'s it. Reset between reps. Add 10 lb from last session.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 10, format: 'recovery', details: 'Decompress after deadlifts — slow and gentle' },
        { name: 'Hip Mobility Flow', sets: 1, reps: 10, format: 'recovery', details: 'Open the hips back up after pulling' },
      ],
    },
  ],
};

const restDay: WorkoutTemplateDay = {
  name: 'Rest Day',
  sessionType: 'pt',
  estimatedDuration: 0,
  blocks: [],
};

export const strengthFoundations12Week: WorkoutProgram = {
  metadata,
  periodization,
  templates: {
    // Testing weeks (W1, W12)
    'testing-week-day1': testingDay,
    'testing-week-day2': restDay,
    'testing-week-day3': restDay,
    'testing-week-day4': restDay,
    'testing-week-day5': restDay,
    'testing-week-day6': restDay,
    'testing-week-day7': restDay,

    // Training cycle — 3 days/week, A B A pattern across the week.
    // (Some programs alternate A B A vs B A B week-to-week, but with
    // a static-template engine the simpler ABA every week still gives
    // novice-frequency squat exposure 3x/week.)
    'training-day1': workoutADay,
    'training-day2': restDay,
    'training-day3': workoutBDay,
    'training-day4': restDay,
    'training-day5': workoutADay,
    'training-day6': restDay,
    'training-day7': restDay,
  },
  workouts: [],
};
